import { defineStore } from 'pinia'
import { asyncRouterMap, constantRouterMap } from '@/router'
import {
  generateRoutesByFrontEnd,
  generateRoutesByServer,
  flatMultiLevelRoutes
} from '@/utils/routerHelper'
import { store } from '../index'
import { cloneDeep, isEqual } from 'lodash-es'
import { securePiniaStorage } from '@/utils/securePinia'
import { Company, fetchInfo, WebSettings, StoreInformation } from '@/api/_core/auth/info'
import { useTagsViewStore } from './tagsView'
import { useAppStore } from './app'

export interface PermissionState {
  routers: AppRouteRecordRaw[]
  addRouters: AppRouteRecordRaw[]
  isAddRouters: boolean
  menuTabRouters: AppRouteRecordRaw[]
  serverRouters: any[]
  permissions: string[]
  company: Company
  webSettings: WebSettings | undefined
  storeInformation: StoreInformation | undefined
}

/**
 * 前端附加的隱藏頁面路由（不需後端選單逐頁設定）
 * 附加在對應父模組（parentPath）之下；使用者無該模組權限時不會附加
 * StoreBotConfig 已改由後端選單設定管理（避免跟這裡重複註冊同名路由造成衝突），目前無項目
 */
const localHiddenRoutes: { parentPath: string; route: AppRouteRecordRaw }[] = []

/**容錯比對：忽略大小寫／前後空白／結尾斜線差異，避免後端選單「網址路徑」欄位打字習慣不同就導致完全比對不到 */
const normalizeRoutePath = (path: string) => path.trim().replace(/\/+$/, '').toLowerCase() || '/'

const appendLocalHiddenRoutes = (routerMap: AppRouteRecordRaw[]) => {
  for (const { parentPath, route } of localHiddenRoutes) {
    const parent = routerMap.find(
      (r) => normalizeRoutePath(r.path) === normalizeRoutePath(parentPath)
    )
    if (!parent) {
      console.warn(
        `[permission] 隱藏路由「${route.name}」未掛載：後端選單資料中找不到 path 相當於 "${parentPath}" 的父層路由（已忽略大小寫/空白/結尾斜線差異仍找不到），` +
          `目前選單頂層路徑為：${routerMap.map((r) => r.path).join(', ') || '(無)'}。` +
          `請確認該選單分類的「網址路徑」欄位。`
      )
      continue
    }
    parent.children = parent.children ?? []
    if (!parent.children.some((c) => c.name === route.name)) {
      parent.children.push(route)
    }
  }
}

export const usePermissionStore = defineStore('permission', {
  state: (): PermissionState => ({
    routers: [],
    addRouters: [],
    isAddRouters: false,
    menuTabRouters: [],

    serverRouters: [],
    permissions: [],
    company: {
      ComName: '',
      IsDesignMode: false,
      EnabledLanguage: false,
      AdminCompanyDescription: '',
      AdminCompanyLogo: '',
      AdminCompanyLoginLogo: '',
      AdminCaptcha: {
        AdminCaptchaProvider: 'None',
        AdminRecaptchaV3SiteKey: '',
        AdminRecaptchaV3ScoreThreshold: '',
        AdminTurnstileSiteKey: ''
      }
    },
    webSettings: undefined,
    storeInformation: undefined
  }),
  getters: {
    getRouters(): AppRouteRecordRaw[] {
      return this.routers
    },
    getAddRouters(): AppRouteRecordRaw[] {
      return flatMultiLevelRoutes(cloneDeep(this.addRouters))
    },
    getIsAddRouters(): boolean {
      return this.isAddRouters
    },
    getMenuTabRouters(): AppRouteRecordRaw[] {
      return this.menuTabRouters
    },
    getServerRouters(): any[] {
      return this.serverRouters
    },
    getPermissions(): string[] {
      return this.permissions
    },
    getCompany(): Company {
      return this.company
    },
    getLanguage() {
      return () =>
        (this.webSettings?.Language || []).map((item) => {
          return { label: item.Country, value: item.Code }
        })
    },
    getSetting() {
      return () => this.webSettings?.Setting
    },
    getStoreInformation(): StoreInformation | undefined {
      return this.storeInformation
    }
  },
  actions: {
    /**設定預設資料 */
    async setInformation() {
      const { data } = await fetchInfo()
      this.company = data.Company
      this.webSettings = data.WebSettings
      if (!isEqual(this.serverRouters, data.Routes)) {
        // 因為 有可能發生 他讀過某一頁 然後頁面權限被移除 所以需要重置
        // 重現情境 先登入後 到資料庫刪除讀取中的頁面權限 然後網頁重新整理 沒有刪除會造成網頁錯誤
        const tagsViewStore = useTagsViewStore()
        tagsViewStore.delAllViews()
      }
      const appStore = useAppStore()
      appStore.setTitle(this.company.ComName)
      this.serverRouters = data.Routes || []
      this.permissions = data.Permissions || []
      this.storeInformation = data.Store
    },
    /**導向頁面 */
    getFirstRedirect(targetRedirect: string = ''): string {
      const rootRoute = this.serverRouters[0]
      if (!rootRoute) return ''

      const path = rootRoute.path
      let redirect = rootRoute.redirect

      // 如果 targetRedirect 存在且路由包含它，則優先使用 targetRedirect
      if (
        targetRedirect &&
        rootRoute.children?.some((child: any) => `${path}/${child.path}` === targetRedirect)
      ) {
        return targetRedirect as string
      }

      // 如果 `redirect` 不在 `children` 內，則改成第一個子路由的 path
      const isRedirectValid = rootRoute.children?.some((x: any) => `${path}/${x.path}` === redirect)
      const firstChildPath = rootRoute.children?.[0]?.path

      // 確保預設路徑存在 (有可能父層的預設路徑，但是子項目沒開啟這個頁面)
      // 當抓不到預設塞入第一個
      if (!isRedirectValid && firstChildPath) {
        redirect = `${path}/${firstChildPath}`
      }

      return redirect
    },
    //=================================================
    generateRoutes(
      type: 'server' | 'frontEnd' | 'static',
      routers?: AppCustomRouteRecordRaw[] | string[]
    ): Promise<unknown> {
      return new Promise<void>((resolve) => {
        let routerMap: AppRouteRecordRaw[] = []
        if (type === 'server') {
          // 模擬後端過濾菜單
          routerMap = generateRoutesByServer(routers as AppCustomRouteRecordRaw[])
        } else if (type === 'frontEnd') {
          // 模擬前端過濾菜單
          routerMap = generateRoutesByFrontEnd(cloneDeep(asyncRouterMap), routers as string[])
        } else {
          // 直接讀取静態路由表
          routerMap = cloneDeep(asyncRouterMap)
        }
        // 附加前端定義的隱藏頁面（如 LINE BOT 設定）
        appendLocalHiddenRoutes(routerMap)
        // 動態路由，404一定要放到最後面
        this.addRouters = routerMap.concat([
          {
            path: '/:path(.*)*',
            redirect: '/404',
            name: '404Page',
            meta: {
              hidden: true,
              breadcrumb: false
            }
          }
        ])

        const _constantRouterMap = cloneDeep(constantRouterMap)
        _constantRouterMap[0].redirect = routerMap[0].redirect

        // 渲染菜單的所有路由
        this.routers = _constantRouterMap.concat(routerMap)
        resolve()
      })
    },
    setIsAddRouters(state: boolean): void {
      this.isAddRouters = state
    },
    setMenuTabRouters(routers: AppRouteRecordRaw[]): void {
      this.menuTabRouters = routers
    }
  },
  persist: [
    {
      pick: ['routers'],
      key: `${securePiniaStorage.prefix('permission')}`,
      storage: securePiniaStorage.storage
    },
    {
      pick: ['addRouters'],
      key: `${securePiniaStorage.prefix('permission')}`,
      storage: securePiniaStorage.storage
    },
    {
      pick: ['menuTabRouters'],
      key: `${securePiniaStorage.prefix('permission')}`,
      storage: securePiniaStorage.storage
    },
    // 選單
    {
      pick: ['serverRouters'],
      key: `${securePiniaStorage.prefix('permission_server_routers')}`,
      storage: securePiniaStorage.storage
    },
    // 權限
    {
      pick: ['permissions'],
      key: `${securePiniaStorage.prefix('permission_server_permissions')}`,
      storage: securePiniaStorage.storage
    },
    // 店家資訊
    {
      pick: ['storeInformation'],
      key: `${securePiniaStorage.prefix('permission_store_information')}`,
      storage: securePiniaStorage.storage
    }
  ]
})

export const usePermissionStoreWithOut = () => {
  return usePermissionStore(store)
}
